import * as _ from 'lodash-es';
import type { Delta, Parchment } from 'quill/core';
import Quill from 'quill/core';
import { useInsertionEffect } from 'react';
import { READONLY_CHANGE_EVENT } from './constants';
import CollapsibleListItem from './formats/CollapsibleListItem';
import ListCollapseFormat from './formats/ListCollapse';
import type { Line } from './types';
import Logger from '../utils/Logger';
import getItemAllLines, { getFirstNonSoftLine } from './utils/getItemAllLines';
import isInlineTextInsertion from './utils/isInlineTextInsertion';

const HIGHLIGHTED_ATTR_NAME = 'data-hovered-list-item';
const COLLAPSE_OVERRIDE_ATTR_NAME = 'data-list-collapse-override';

class ListCollapseManager {
  private isReadOnly: boolean;
  private stylesScope: string;

  private editModeCollapsedItems = new Set<CollapsibleListItem>();
  private readOnlyCollapsedItems = new Set<CollapsibleListItem>();
  private styleContainer = document.createElement('style');

  private highlightedItem: CollapsibleListItem | null = null;

  private localStateListeners: (() => void)[] = [];

  constructor(
    private quill: Quill,
    options: { styleScope: string },
  ) {
    this.stylesScope = options.styleScope;

    this.quill.on(READONLY_CHANGE_EVENT, this.handleEditorReadOnlyChanged);
    this.quill.on(Quill.events.TEXT_CHANGE, this.handleTextChange);
    this.quill.container.ownerDocument.head.appendChild(this.styleContainer);

    quill.scroll
      .descendants(CollapsibleListItem)
      .filter((item) => item.isGloballyCollapsed())
      .forEach((item) => {
        this.editModeCollapsedItems.add(item);
        this.readOnlyCollapsedItems.add(item);
      });

    this.isReadOnly = !quill.isEnabled();
    this.updateCollapsedStyles();
  }

  destroy() {
    this.quill.off(Quill.events.TEXT_CHANGE, this.handleTextChange);
    this.quill.off(READONLY_CHANGE_EVENT, this.handleEditorReadOnlyChanged);
    this.styleContainer.remove();
    this.localStateListeners = [];
  }

  toggleCollapse(
    item: Parchment.Blot,
    forceIsCollapsed?: boolean,
  ): { isCollapsed: boolean } {
    const blot =
      item instanceof CollapsibleListItem ? getFirstNonSoftLine(item) : null;

    if (!blot) {
      return { isCollapsed: false };
    }

    // In read mode, we locally override the collapse state changes
    // to prevent users from being disturbed.
    if (this.isReadOnly) {
      const isCollapsed = this.readOnlyCollapsedItems.has(blot);
      if (isCollapsed === forceIsCollapsed) {
        return { isCollapsed };
      }
      if (isCollapsed) {
        this.readOnlyCollapsedItems.delete(blot);
        blot.domNode.setAttribute(COLLAPSE_OVERRIDE_ATTR_NAME, 'false');
      } else {
        this.readOnlyCollapsedItems.add(blot);
        blot.domNode.setAttribute(COLLAPSE_OVERRIDE_ATTR_NAME, 'true');
      }
      this.updateCollapsedStyles();
      this.localStateListeners.forEach((fn) => fn());
      return { isCollapsed: !isCollapsed };
    }

    if (blot.isGloballyCollapsed() === forceIsCollapsed) {
      return { isCollapsed: !!forceIsCollapsed };
    }

    const isCollapsedNew = forceIsCollapsed ?? !blot.isGloballyCollapsed();
    blot.setIsGloballyCollapsed(isCollapsedNew);
    return { isCollapsed: isCollapsedNew };
  }

  revealBlot(blot: Parchment.Blot) {
    const closestListItem = this.getClosestListItem(blot);
    if (!closestListItem) {
      return;
    }
    let lastExpandedItemIndent = closestListItem.getIndent();
    for (
      let currentItem: Parchment.Blot | null = closestListItem;
      currentItem instanceof CollapsibleListItem;
      currentItem = currentItem.prev
    ) {
      const indent = currentItem.getIndent();
      const isSoftLine = currentItem.isSoftLine();

      if (indent < lastExpandedItemIndent && !isSoftLine) {
        this.toggleCollapse(currentItem, false);
        lastExpandedItemIndent = currentItem.getIndent();
      }
    }
  }

  subscribeToLocalCollapseStateChange = (callback: () => void) => {
    this.localStateListeners.push(callback);
    return () => {
      this.localStateListeners = this.localStateListeners.filter(
        (fn) => fn !== callback,
      );
    };
  };

  setHighlightedListItem(blot: Parchment.Blot | null) {
    if (blot === this.highlightedItem) {
      return;
    }

    this.highlightedItem?.domNode.removeAttribute(HIGHLIGHTED_ATTR_NAME);
    this.highlightedItem = blot instanceof CollapsibleListItem ? blot : null;
    this.highlightedItem?.domNode.setAttribute(HIGHLIGHTED_ATTR_NAME, 'true');
  }

  isCollapsed(line: Line) {
    const firstLine = getFirstNonSoftLine(line);

    if (!(firstLine instanceof CollapsibleListItem)) {
      return false;
    }

    return this.isReadOnly
      ? this.readOnlyCollapsedItems.has(firstLine)
      : this.editModeCollapsedItems.has(firstLine);
  }

  getNestedListItems(blot: Line) {
    if (!(blot instanceof CollapsibleListItem)) {
      return [];
    }

    const indent = blot.getIndent();
    const blotLines = getItemAllLines(blot);
    const result: CollapsibleListItem[] = [];

    let currentBlot = blotLines.at(-1)?.next;
    while (
      currentBlot instanceof CollapsibleListItem &&
      currentBlot.getIndent() > indent
    ) {
      result.push(currentBlot);
      currentBlot = currentBlot.next;
    }

    return result;
  }

  private getClosestListItem(blot: Parchment.Blot): CollapsibleListItem | null {
    let closestListItem = blot;
    while (
      closestListItem != null &&
      !(closestListItem instanceof CollapsibleListItem)
    ) {
      closestListItem = closestListItem.parent;
    }
    return closestListItem;
  }

  private handleTextChange = (delta: Delta) => {
    if (isInlineTextInsertion(delta)) {
      return;
    }

    this.quill.scroll.descendants(CollapsibleListItem).forEach((item) => {
      // In edit mode reflect every change in the collapsed state
      if (item.isGloballyCollapsed()) {
        this.editModeCollapsedItems.add(item);
      } else {
        this.editModeCollapsedItems.delete(item);
      }

      // In read mode maintain collapse state to avoid disturbing the user
      if (this.isReadOnly) {
        const isCollapsedLocally = this.readOnlyCollapsedItems.has(item);
        const isCollapsedGlobally = this.editModeCollapsedItems.has(item);
        if (isCollapsedGlobally !== isCollapsedLocally) {
          item.domNode.setAttribute(
            COLLAPSE_OVERRIDE_ATTR_NAME,
            isCollapsedLocally ? 'true' : 'false',
          );
        }
      }
    });

    if (!this.isReadOnly) {
      this.updateCollapsedStyles();
    }
  };

  private handleEditorReadOnlyChanged = (newReadOnly: boolean) => {
    this.isReadOnly = newReadOnly;
    if (this.isReadOnly) {
      this.readOnlyCollapsedItems = new Set(this.editModeCollapsedItems);
    } else {
      this.quill.root
        .querySelectorAll(`[${COLLAPSE_OVERRIDE_ATTR_NAME}]`)
        .forEach((node) => node.removeAttribute(COLLAPSE_OVERRIDE_ATTR_NAME));
    }
    this.updateCollapsedStyles();
    this.localStateListeners.forEach((fn) => fn());
  };

  private updateCollapsedStyles() {
    const items = this.isReadOnly
      ? this.readOnlyCollapsedItems
      : this.editModeCollapsedItems;

    const selectors = Array.from(items).map((item) => {
      const indent = item.getIndent();
      const currentItem = `[${CollapsibleListItem.ID_ATTR_NAME}='${item.id}']`;
      const followingItemsWithHigherIndent = `${currentItem} ~ [${CollapsibleListItem.INDENT_MAP_ATTR_NAME}~='${indent + 1}']`;
      const followingItemsAfterItemWithLowerOrEqualIndent = `${currentItem} ~ [${CollapsibleListItem.INDENT_MAP_ATTR_NAME}~='-${indent + 1}']:not(.ql-soft-true) ~ *`;

      // Hide item for user in a way that's friendly for the magnetic wall
      return `
        ${this.stylesScope} ${followingItemsWithHigherIndent}:not(${followingItemsAfterItemWithLowerOrEqualIndent}) {
          content-visibility: hidden;
          position: absolute;
          width: 0;
          height: 0;
          padding: 0;
          overflow: hidden;
        }
      `;
    });

    this.styleContainer.innerText = selectors
      .join('')
      .replaceAll(/[\r\n]/g, '');
  }
}

const instances = new WeakMap<Quill, ListCollapseManager>();
export const getListCollapse = (quill: Quill) => instances.get(quill);

const REQUIRED_FORMATS = [ListCollapseFormat];
const useListCollapse = (quill: Quill | null) => {
  useInsertionEffect(() => {
    if (!quill) return undefined;

    if (instances.has(quill)) {
      Logger.error(
        'ListCollapseManager already exists for this Quill instance. You might have called `useHeaderCollapse()` multiple times.',
      );
    }

    REQUIRED_FORMATS.forEach((format) => {
      if (!quill.scroll.query(format.attrName)) {
        Logger.error(
          `formats/${format.attrName} is required for ListCollapse.`,
        );
      }
    });

    const className = _.uniqueId('list-collapse-scope-');
    quill.container.classList.add(className);
    const instance = new ListCollapseManager(quill, {
      styleScope: `.${className} > .ql-editor`,
    });
    instances.set(quill, instance);
    return () => {
      instances.delete(quill);
      instance.destroy();
      quill.container.classList.remove(className);
    };
  }, [quill]);
};

export default useListCollapse;
